import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import Loading from 'Components/smallComponents/loading';
import type { TemplateFunction, TemplateFunctionInvoker, TemplateResult } from 'Components/types';
import { loadMore as loadMoreAction } from 'Core/actions/request';
import {
  allItemsReceivedSelector,
  nextPageSizeSelector,
  noItemsReceivedSelector,
} from 'Core/selectors/search';
import { infiniteScrollLoadingSelector, resultsLoadingSelector } from 'Core/selectors/show';
import { useDispatch, useSelector } from 'Core/store';
import { cloneSafe } from 'Utils/components';

interface Props {
  template: TemplateFunction<LoadMoreControllerTemplateParams>;
  isInstant?: boolean;
  isInfiniteScrollDisabled?: boolean;
  isLoadMoreFailed?: boolean;
}

export interface LoadMoreButtonTemplateParams {
  activate: () => void;
  nextPageSize: number;
}

export interface LoadMoreControllerTemplateParams {
  isActive: boolean;
  isNotAllItemsReceived: boolean;
  loadMoreButton: TemplateFunctionInvoker<LoadMoreButtonTemplateParams>;
  loading: TemplateResult;
}

export default function LoadMoreController({
  template,
  isInstant,
  isInfiniteScrollDisabled,
  isLoadMoreFailed,
}: Props): TemplateResult {
  const [isActive, setIsActive] = useState<boolean>(false);
  const rootRef = useRef<HTMLElement>(null);

  const dispatch = useDispatch();

  const loadMore = useCallback(() => dispatch(loadMoreAction()), [dispatch]);

  const isInfiniteScrollLoading = useSelector(infiniteScrollLoadingSelector);
  const isResultsLoading = useSelector(resultsLoadingSelector);
  const isLoading = isInfiniteScrollLoading || isResultsLoading;

  const isAllItemsReceived = useSelector(allItemsReceivedSelector);
  const isNoItemsReceived = useSelector(noItemsReceivedSelector);

  const pageNumber = useSelector((state) => state.search.request.pageNumber);
  const prevPageNumber = useRef(pageNumber);

  const nextPageSize = useSelector(nextPageSizeSelector);

  // animationFrame loop has to be started just once but we need to update callback in it.
  // using ref we may not to change frame() instance but are still able to vary the code within
  const inFrameCallbackRef = useRef<(() => void) | undefined>(undefined);
  inFrameCallbackRef.current = useCallback(() => {
    const root = rootRef.current;
    // if the root or its parent has `{ display: none }`, root.offsetParent is null
    if (!root || !root.offsetParent) {
      return;
    }
    const clientRectTop = root.getBoundingClientRect().top;
    const isCloseToViewport = clientRectTop <= window.innerHeight;
    if (
      isActive &&
      !isLoadMoreFailed &&
      !isLoading &&
      !isAllItemsReceived &&
      isCloseToViewport &&
      nextPageSize > 0
    ) {
      loadMore();
    }
  }, [isActive, isAllItemsReceived, isLoading, loadMore, isLoadMoreFailed, nextPageSize]);

  const activate = useCallback(() => {
    if (isInfiniteScrollDisabled) {
      loadMore();
      return;
    }

    // if element has not been rendered yet
    if (!rootRef.current) {
      return;
    }
    setIsActive(true);
    window.requestAnimationFrame(function frame() {
      inFrameCallbackRef.current?.();
      window.requestAnimationFrame(frame);
    });
  }, [isInfiniteScrollDisabled, loadMore]);

  useEffect(() => {
    if (pageNumber < prevPageNumber.current) {
      setIsActive(false);
    }
    prevPageNumber.current = pageNumber;

    if (isInstant && !isActive) {
      activate();
    }
  }, [activate, isActive, isInstant, pageNumber]);

  const loading = <Loading loading={isLoading} key="loading" />;
  const loadMoreButton = (templ: TemplateFunction<LoadMoreButtonTemplateParams>) =>
    cloneSafe(templ.call({ activate, nextPageSize }), null, { key: 'button' });

  const component = template.call({
    loading,
    loadMoreButton,
    isNotAllItemsReceived: !isNoItemsReceived && !isAllItemsReceived,
    isActive,
  });
  return cloneSafe(component, rootRef);
}
